import React, { lazy, Suspense, useMemo, useState } from "react";
import FormattedText from "../../../FormattedText/FormattedText";
import type { TextNodeType } from "../../../FormattedText/FormattedText";
import type { MyCashState } from "../../ListingPageV2.types";
import { DownloadBenefitsBottomsheet } from "../DownloadBenefitsBottomsheet/DownloadBenefitsBottomsheet";
const MoreBenefits = lazy(
  () => import("../../../InsurancePlans/MoreBenefits/MoreBenefits")
);
import {
  ActionsRow,
  Badge,
  BadgeText,
  BenefitCover,
  BenefitIcon,
  BenefitRow,
  BenefitTitle,
  Benefits,
  BenefitsLink,
  BottomActionsRow,
  Card,
  CardCurve,
  CardInner,
  CompareBtn,
  CompareLabel,
  CompareWrap,
  CurrentPrice,
  Divider,
  DownloadBtn,
  DownloadLabel,
  Footer,
  FooterIcon,
  FooterText,
  Header,
  PlanIconImg,
  OriginalPrice,
  PersuasionIcon,
  PersuasionTag,
  PersuasionText,
  PriceCol,
  PriceRow,
  Title,
  TitleCol,
  TitleRow,
  Unit,
  VendorLogo,
  VendorName,
  Wrapper,
} from "./Styles";

import { DownloadIcon as DownloadSvg } from "../../../CommonLandingMweb/shared/MwebIcons";
import CheckButton from "../../../CheckButton/CheckButton";

export interface PlanCardProps {
  planId?: string;
  productId?: number;
  badge?: {
    text?: string;
    styles?: { bgColorGradient?: string[]; color?: string };
  };
  icon?: string;
  planIcon?: string;
  subtitle?: string;
  title?: string;
  prices?: {
    originalPrice?: string;
    discountedPrice?: string;
    mycashAppliedPrice?: string;
  };
  unit?: string;
  headerPersuation?: {
    generic?: { icon?: string; text?: string };
    mycashApplied?: { icon?: string; text?: string };
  };
  previewBenefits?: Array<{ icon?: string; title?: string; desc?: string }>;
  cta?: { benefitsText?: string; downloadText?: string };
  showCompare?: boolean;
  footerPersuation?: { icon?: string; title?: TextNodeType };
  benefitsBottomsheet?: import("../../ListingPageV2.types").PlanBenefitsBottomsheet | Record<string, any>;
  downloadBottomsheet?: import("../../ListingPageV2.types").DownloadBenefitsBottomsheetData;
  onCompareToggle?: (planId: string, selected: boolean) => void;
  isCompareSelected?: boolean;
  myCashState?: MyCashState;
  csrfToken?: string;
  onBuyNow?: (ctx: {
    quoteId: string;
    productId?: number;
    myCashApplied: boolean;
  }) => void;
  onTncOpen?: (url: string, title?: string) => void;
}

function transformBottomsheetForMoreBenefits(
  bs: Record<string, any>,
  planId?: string,
  productId?: number,
  prices?: { discountedPrice?: string },
  unit?: string,
  csrfToken?: string
) {
  const powerBy = bs["PowerBy"];
  const powerByText =
    typeof powerBy?.text === "object" && !Array.isArray(powerBy.text)
      ? powerBy.text.value
      : powerBy?.text;

  const tnc = bs["tnc"];
  const dropdown = bs["downloadBenefitsDropdown"];

  return {
    productId: String(productId ?? ""),
    quoteId: planId || "",
    vendorDisplayPrefix: powerByText || "Powered by",
    insuranceType: bs["insPlanName"],
    service: bs["disclaimer"],
    packName: bs["titleDownloadBenefit"],
    tncLink: tnc?.link,
    links: [
      ...(tnc
        ? [
            {
              id: "tnc",
              title: tnc.title,
              type: "link" as const,
              url: tnc.link,
            },
          ]
        : []),
      ...(dropdown
        ? [
            {
              id: "download_benefits",
              title: dropdown.header?.title || {
                text: { value: "Download Benefits" },
              },
              type: "dropdown" as const,
              dropdownOptions:
                dropdown.options?.map((option: any) => ({
                  title: option.label || { text: { value: "" } },
                  url: "",
                  templateType: option.id || "with_price",
                  ctaText: { text: { value: "DOWNLOAD NOW" } },
                  defaultSelected: option.defaultSelected || false,
                  id: option.id || "with_price",
                })) || [],
            },
          ]
        : []),
    ],
    totalBenefits: bs["totalBenefits"],
    moreBenefits: bs["moreBenefits"],
    insPlanName: bs["insProvider"],
    insAmount: prices?.discountedPrice
      ? { text: { value: prices.discountedPrice } }
      : undefined,
    paxPerDay: unit ? { text: { value: unit } } : undefined,
    buyBtn: "BUY NOW",
    csrfToken,
  };
}

function gradientCss(colors?: string[]): string {
  const c = colors?.length ? colors : ["#008cff", "#0066cc"];
  if (c.length === 1) return c[0];
  return `linear-gradient(90deg, ${c.join(", ")})`;
}

export const PlanCard = ({
  planId,
  productId,
  badge,
  icon,
  planIcon,
  subtitle,
  title,
  prices,
  unit,
  headerPersuation,
  previewBenefits,
  cta,
  showCompare = true,
  footerPersuation,
  benefitsBottomsheet,
  downloadBottomsheet,
  csrfToken,
  onCompareToggle,
  isCompareSelected = false,
  myCashState = "unApplied",
  onBuyNow,
  onTncOpen,
}: PlanCardProps) => {
  const [benefitsOpen, setBenefitsOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const isMyCashApplied = myCashState === "applied";

  const moreBenefitsData = useMemo(() => {
    if (!benefitsBottomsheet) return undefined;
    return transformBottomsheetForMoreBenefits(
      benefitsBottomsheet as Record<string, any>,
      planId,
      productId,
      prices,
      unit,
      csrfToken
    );
  }, [benefitsBottomsheet, planId, productId, prices, unit, csrfToken]);

  const moreBenefitsTitle = useMemo(() => {
    if (!benefitsBottomsheet) return "Benefits";
    return (
      (benefitsBottomsheet as Record<string, any>)["totalBenefits"] ||
      "Benefits"
    );
  }, [benefitsBottomsheet]);

  const currentPrice = isMyCashApplied
    ? prices?.mycashAppliedPrice
    : prices?.discountedPrice;

  const persuasion = isMyCashApplied
    ? headerPersuation?.mycashApplied
    : headerPersuation?.generic;

  const borderColor = badge?.styles?.bgColorGradient?.[0];

  const handleBuy = () => {
    if (!planId) return;
    onBuyNow?.({
      quoteId: planId,
      productId,
      myCashApplied: isMyCashApplied,
    });
    setBenefitsOpen(false);
  };

  const onCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleBuy();
    }
  };

  return (
    <Wrapper>
      <Card
        $borderColor={borderColor}
        $badgePad={Boolean(badge?.text)}
      >
        <CardInner
          role="button"
          tabIndex={0}
          onClick={handleBuy}
          onKeyDown={onCardKeyDown}
        >
          {badge?.text ? (
            <Badge $gradient={gradientCss(badge.styles?.bgColorGradient)}>
              <BadgeText $color={badge.styles?.color}>
                {badge.text.toUpperCase()}
              </BadgeText>
            </Badge>
          ) : null}

          <Header>
            <TitleCol>
              <TitleRow>
                {planIcon ? (
                  <PlanIconImg src={planIcon} alt="" />
                ) : icon ? (
                  <VendorLogo src={icon} alt="" />
                ) : null}
                <Title>{title}</Title>
              </TitleRow>
              {subtitle ? (
                <VendorName>{subtitle}</VendorName>
              ) : null}
            </TitleCol>
            <PriceCol>
              <PriceRow>
                {prices?.originalPrice ? (
                  <OriginalPrice>{prices.originalPrice}</OriginalPrice>
                ) : null}
                {currentPrice ? (
                  <CurrentPrice>{currentPrice}</CurrentPrice>
                ) : null}
                {unit ? <Unit>{unit}</Unit> : null}
              </PriceRow>
              {persuasion?.text ? (
                <PersuasionTag>
                  {persuasion.icon ? (
                    <PersuasionIcon src={persuasion.icon} alt="" />
                  ) : null}
                  <PersuasionText>{persuasion.text}</PersuasionText>
                </PersuasionTag>
              ) : null}
            </PriceCol>
          </Header>

          <Divider />

          <Benefits>
            {previewBenefits?.map((benefit, index) => (
              <BenefitRow key={`${benefit.title}-${index}`}>
                {benefit.icon ? (
                  <BenefitIcon src={benefit.icon} alt="" />
                ) : null}
                <BenefitTitle>{benefit.title}</BenefitTitle>
                {benefit.desc ? (
                  <BenefitCover>{benefit.desc}</BenefitCover>
                ) : null}
              </BenefitRow>
            ))}
          </Benefits>

        </CardInner>

        <BottomActionsRow>
          {showCompare ? (
            <CompareWrap>
              <CardCurve aria-hidden />
              <CompareBtn>
                <CheckButton
                  inputId={`compare-${planId}`}
                  isChecked={isCompareSelected}
                  handleChange={(e) => {
                    e.stopPropagation();
                    onCompareToggle?.(planId || "", !isCompareSelected);
                  }}
                >
                  <CompareLabel>Compare</CompareLabel>
                </CheckButton>
              </CompareBtn>
            </CompareWrap>
          ) : cta?.downloadText ? (
            <ActionsRow>
              <DownloadBtn
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (downloadBottomsheet) setDownloadOpen(true);
                }}
              >
                <DownloadSvg />
                <DownloadLabel>{cta.downloadText}</DownloadLabel>
              </DownloadBtn>
            </ActionsRow>
          ): null}
          {cta?.benefitsText ? (
            <BenefitsLink
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setBenefitsOpen(true);
              }}
            >
              {cta.benefitsText} &gt;
            </BenefitsLink>
          ) : null}
        </BottomActionsRow>
      </Card>

      {footerPersuation?.title ? (
        <Footer>
          {footerPersuation.icon ? (
            <FooterIcon src={footerPersuation.icon} alt="" />
          ) : null}
          <FooterText>
            <FormattedText
              value={footerPersuation.title}
              baseStyle={{ fontSize: 12, color: "#000" }}
            />
          </FooterText>
        </Footer>
      ) : null}

      {moreBenefitsData && benefitsOpen ? (
        <Suspense fallback={null}>
          <MoreBenefits
            moreBenfitsTitle={moreBenefitsTitle}
            data={moreBenefitsData as any}
            handleClose={() => setBenefitsOpen(false)}
            handleBuyButton={() => { setBenefitsOpen(false); handleBuy(); }}
          />
        </Suspense>
      ) : null}

      {downloadBottomsheet ? (
        <DownloadBenefitsBottomsheet
          open={downloadOpen}
          onClose={() => setDownloadOpen(false)}
          data={downloadBottomsheet}
          quoteId={planId}
          csrfToken={csrfToken}
        />
      ) : null}
    </Wrapper>
  );
};
